import { useState } from "react";
import { IconAlertTriangle, IconLink, IconMail, IconPaperclip } from "@tabler/icons-react";
import { Trans } from "@lingui/macro";

import { analytics } from "../../analytics/analyticsEvents";
import { getContractById, skills as allSkills, tools as allTools } from "../../content";
import { useLocalize } from "../../content/useLocalize";
import { suitableSkillsForType, suitableToolsForType } from "../../engine/loadout";
import { externalSenderIndices, linkMismatchIndices, phishingCount } from "../../engine/phishingHints";
import { calculatePhishingScore } from "../../engine/phishingScore";
import { getGrade } from "../../engine/scoring";
import { useGameStore } from "../../state/store";
import { Button } from "../common/Button";
import { EmptyState } from "../common/EmptyState";
import { GradeSeal } from "../common/GradeSeal";
import { Panel } from "../common/Panel";
import { StatTile } from "../common/StatTile";
import { cx } from "../common/cx";
import { PICK_VERDICT_VIEW, countPickVerdicts, pickVerdictFor } from "../common/pickVerdict";
import { useGameplaySession } from "../common/useGameplaySession";
import { LoadoutPicker } from "../loadout/LoadoutPicker";
import { useLoadoutNames } from "../loadout/useLoadoutNames";
import styles from "./PhishingInbox.module.css";

export function PhishingInbox(): JSX.Element {
  const t = useLocalize();
  const { skillName, toolName } = useLoadoutNames();
  const activeContractId = useGameStore((state) => state.activeContractId);
  const activeToolIds = useGameStore((state) => state.activeToolIds);
  const activeSkillIds = useGameStore((state) => state.activeSkillIds);
  const player = useGameStore((state) => state.player);
  const toggleActiveTool = useGameStore((state) => state.toggleActiveTool);
  const toggleActiveSkill = useGameStore((state) => state.toggleActiveSkill);
  const phishingLevel = useGameStore((state) =>
    state.activeSkillIds.includes("phishing") ? (state.player.skills.phishing ?? 0) : 0,
  );
  const navigateTo = useGameStore((state) => state.navigateTo);
  const completeTask = useGameStore((state) => state.completeTask);

  const contract = activeContractId ? getContractById(activeContractId) : undefined;
  const task = contract?.phishing;
  const emails = task?.emails ?? [];

  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);

  useGameplaySession("phishing");

  const senderCheck = !submitted && activeToolIds.includes("sender_check");
  const linkPreview = !submitted && activeToolIds.includes("link_preview");
  const externalIndices = task && senderCheck ? new Set(externalSenderIndices(task)) : null;
  const mismatchIndices = task && linkPreview ? new Set(linkMismatchIndices(task)) : null;
  const countHint = task && !submitted && phishingLevel >= 1 ? phishingCount(task) : null;

  if (!contract || !task || emails.length === 0) {
    return (
      <EmptyState
        title={<Trans>Нет писем для разбора</Trans>}
        action={
          <Button variant="secondary" onClick={() => navigateTo("workspace")}>
            <Trans>Назад</Trans>
          </Button>
        }
      />
    );
  }

  const toggle = (index: number): void => {
    if (submitted) {
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const submit = (): void => {
    const result = calculatePhishingScore(emails, selected, phishingLevel);
    setScore(result);
    setSubmitted(true);
    analytics.taskSubmitted({ type: "phishing", score: result });
  };

  const finish = (): void => {
    completeTask(score);
    navigateTo("workspace");
  };

  const pickerTools = suitableToolsForType("phishing", player.ownedTools, allTools);
  const pickerSkills = suitableSkillsForType("phishing", player.skills, allSkills);
  const grade = getGrade(score);
  const verdictCounts = countPickVerdicts(
    emails.map((email) => ({ positive: email.isPhishing })),
    selected,
  );
  const firstPhishingIndex = emails.findIndex((email) => email.isPhishing);

  return (
    <div className={styles.screen}>
      <LoadoutPicker
        tools={pickerTools}
        skills={pickerSkills}
        skillLevels={player.skills}
        activeToolIds={activeToolIds}
        activeSkillIds={activeSkillIds}
        readOnly={submitted}
        onToggleTool={toggleActiveTool}
        onToggleSkill={toggleActiveSkill}
      />
      <Panel
        title={<Trans>Входящие клиента</Trans>}
        actions={
          submitted ? (
            <span className={cx(styles.scoreTag, "mono")}>
              <Trans>Очки: {score}</Trans>
            </span>
          ) : undefined
        }
      >
        {submitted ? (
          <div className={styles.result}>
            <GradeSeal grade={grade} score={score} />
            <div className={styles.resultStats}>
              <StatTile label={<Trans>Верно</Trans>} value={`${verdictCounts.correct}`} tone="accent" />
              <StatTile label={<Trans>Ложные</Trans>} value={`${verdictCounts.falsePositive}`} />
              <StatTile label={<Trans>Пропущено</Trans>} value={`${verdictCounts.missed}`} />
              <StatTile label={<Trans>Счёт</Trans>} value={`${score}`} />
            </div>
          </div>
        ) : (
          <p className={styles.hint}>
            <Trans>Отметь письма, которые выглядят как фишинг: чужой адрес, срочность, просьба ввести пароль или оплатить.</Trans>
            {countHint !== null && (
              <span className={styles.skillHint}>
                {" "}
                {skillName("phishing")}: <Trans>фишинговых писем - {countHint}</Trans>
              </span>
            )}
          </p>
        )}

        <ul className={styles.rows}>
          {emails.map((email, index) => {
            const picked = selected.has(index);
            const verdict = pickVerdictFor(email.isPhishing, picked);
            const external = externalIndices?.has(index) ?? false;
            const mismatch = mismatchIndices?.has(index) ?? false;
            return (
              <li
                key={index}
                data-tutorial={index === firstPhishingIndex ? "phishing-row" : undefined}
                className={cx(
                  styles.row,
                  (external || mismatch) && styles.hinted,
                  picked && !submitted && styles.picked,
                  submitted && styles[verdict],
                )}
              >
                {(external || mismatch) && (
                  <div className={styles.toolHint}>
                    {external && (
                      <span>
                        {toolName("sender_check")}: <Trans>адрес вне доменов клиента</Trans>
                      </span>
                    )}
                    {mismatch && (
                      <span>
                        {toolName("link_preview")}: <Trans>ссылка ведёт на чужой домен</Trans>
                      </span>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className={styles.rowMain}
                  onClick={() => toggle(index)}
                  aria-pressed={picked}
                  disabled={submitted}
                >
                  <span className={styles.rowHead}>
                    <IconMail size={15} aria-hidden="true" className={styles.mailIcon} />
                    <span className={styles.sender}>{t(email.from.name)}</span>
                    <span className={cx(styles.address, "mono")}>{email.from.address}</span>
                    <span className={cx(styles.time, "mono")}>{email.receivedAt}</span>
                  </span>
                  <span className={styles.subject}>{t(email.subject)}</span>
                  <span className={styles.preview}>{t(email.preview)}</span>
                  {(email.link || email.attachment) && (
                    <span className={styles.chips}>
                      {email.link && (
                        <span className={cx(styles.chip, "mono")}>
                          <IconLink size={13} aria-hidden="true" />
                          {t(email.link.text)}
                          {linkPreview && <span className={styles.href}> -&gt; {email.link.href}</span>}
                        </span>
                      )}
                      {email.attachment && (
                        <span className={cx(styles.chip, "mono")}>
                          <IconPaperclip size={13} aria-hidden="true" />
                          {email.attachment}
                        </span>
                      )}
                    </span>
                  )}
                  <span className={styles.flag}>
                    {picked ? (
                      <span className={styles.flagOn}>
                        <IconAlertTriangle size={15} aria-hidden="true" />
                        <Trans>Фишинг</Trans>
                      </span>
                    ) : (
                      <span className={styles.flagOff}>
                        <Trans>Норма</Trans>
                      </span>
                    )}
                  </span>
                </button>
                {submitted && (
                  <div className={styles.feedback}>
                    <span className={styles.verdict}>
                      {PICK_VERDICT_VIEW[verdict].icon}
                      {PICK_VERDICT_VIEW[verdict].label}
                    </span>
                    <span className={styles.explanation}>{t(email.explanation)}</span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <div className={styles.footer}>
          {submitted ? (
            <span data-tutorial="task-continue">
              <Button variant="primary" onClick={finish}>
                <Trans>Продолжить</Trans>
              </Button>
            </span>
          ) : (
            <>
              {selected.size === 0 && (
                <span className={styles.footerHint}>
                  <Trans>Отметь хотя бы одно письмо, чтобы проверить.</Trans>
                </span>
              )}
              <Button variant="primary" onClick={submit} disabled={selected.size === 0}>
                <Trans>Проверить</Trans>
              </Button>
            </>
          )}
        </div>
      </Panel>
    </div>
  );
}
